/**
 * Pool Homogeneity Validator
 * 
 * Validates that a candidate pool is homogeneous - meaning all items share:
 * - The same candidate type (problem, opportunity, solution, etc.)
 * - The same altitude level (task, story, feature, epic, initiative, outcome)
 * 
 * Per the Prioritization Session Model spec:
 * "You cannot prioritize across types or across altitudes without first collapsing
 * them into a coherent comparison space — and that collapse is a design decision,
 * not a UI feature."
 * 
 * This validator enforces this invariant.
 */

import type { Candidate, CandidateLanguageType } from '../types/prioritization/lifecycle';
import type { AltitudeLevel } from '../types/prioritization/itemLifecycle';

// ============================================================================
// Types
// ============================================================================

/**
 * Homogeneity blocker - describes why a pool is not homogeneous
 */
export interface PoolHomogeneityBlocker {
  code: 'MIXED_CANDIDATE_TYPES' | 'MIXED_ALTITUDES' | 'MISSING_TYPE' | 'MISSING_ALTITUDE';
  title: string;
  description: string;
  currentState: string;
  affectedItems: string[];  // Item IDs
  remediationAction: string;
  remediationRoute?: string;
}

/**
 * Result of pool homogeneity validation
 */
export interface PoolHomogeneityResult {
  /** Whether all items are homogeneous (same type AND same altitude) */
  isHomogeneous: boolean;
  
  /** Whether all items have the same candidate type */
  isTypeHomogeneous: boolean;
  
  /** Whether all items have the same altitude level */
  isAltitudeHomogeneous: boolean;
  
  /** Unique candidate types found in the pool */
  candidateTypes: Set<CandidateLanguageType>;
  
  /** Unique altitude levels found in the pool */
  altitudes: Set<AltitudeLevel>;
  
  /** Items missing candidate type */
  itemsMissingType: string[];
  
  /** Items missing altitude level */
  itemsMissingAltitude: string[];
  
  /** The dominant candidate type (most common) */
  dominantType?: CandidateLanguageType;
  
  /** The dominant altitude level (most common) */
  dominantAltitude?: AltitudeLevel;
  
  /** Blockers preventing homogeneity */
  blockers: PoolHomogeneityBlocker[];
  
  /** Summary statistics */
  stats: {
    totalItems: number;
    typeCounts: Record<string, number>;
    altitudeCounts: Record<string, number>;
  };
}

// ============================================================================
// Validation Functions
// ============================================================================

/**
 * Validate that a pool of candidates is homogeneous
 * 
 * @param items - Array of candidates to validate
 * @param options - Validation options
 * @returns Homogeneity validation result
 */
export function validatePoolHomogeneity(
  items: Candidate[],
  options: {
    requireExplicitType?: boolean;
    requireExplicitAltitude?: boolean;
  } = {}
): PoolHomogeneityResult {
  const {
    requireExplicitType = true,
    requireExplicitAltitude = true,
  } = options;
  
  const candidateTypes = new Set<CandidateLanguageType>();
  const altitudes = new Set<AltitudeLevel>();
  const typeCounts: Record<string, number> = {};
  const altitudeCounts: Record<string, number> = {};
  const itemsMissingType: string[] = [];
  const itemsMissingAltitude: string[] = [];
  const blockers: PoolHomogeneityBlocker[] = [];
  
  // Analyze each item
  for (const item of items) {
    // Check candidate type
    if (item.itemCategory) {
      candidateTypes.add(item.itemCategory);
      typeCounts[item.itemCategory] = (typeCounts[item.itemCategory] || 0) + 1;
    } else if (requireExplicitType) {
      itemsMissingType.push(item.id);
    }
    
    // Check altitude level
    const altitude = item.altitudeLevel;
    if (altitude !== undefined && altitude !== null) {
      altitudes.add(altitude);
      altitudeCounts[String(altitude)] = (altitudeCounts[String(altitude)] || 0) + 1;
    } else if (requireExplicitAltitude) {
      itemsMissingAltitude.push(item.id);
    }
  }
  
  // Determine dominant type and altitude
  const dominantType = findDominant(typeCounts) as CandidateLanguageType | undefined;
  const dominantAltitude = findDominant(altitudeCounts) 
    ? Number(findDominant(altitudeCounts)) as AltitudeLevel 
    : undefined;
  
  // Check homogeneity
  const isTypeHomogeneous = candidateTypes.size <= 1 && itemsMissingType.length === 0;
  const isAltitudeHomogeneous = altitudes.size <= 1 && itemsMissingAltitude.length === 0;
  const isHomogeneous = isTypeHomogeneous && isAltitudeHomogeneous;
  
  // Generate blockers
  if (itemsMissingType.length > 0) {
    blockers.push({
      code: 'MISSING_TYPE',
      title: 'Items Missing Candidate Type',
      description: `${itemsMissingType.length} item(s) do not have an explicit candidate type assigned.`,
      currentState: `${itemsMissingType.length} of ${items.length} items missing type`,
      affectedItems: itemsMissingType,
      remediationAction: 'Assign a candidate type (problem, opportunity, solution, etc.) to each item.',
      remediationRoute: '/prioritization/shape/queue',
    });
  }
  
  if (itemsMissingAltitude.length > 0) {
    blockers.push({
      code: 'MISSING_ALTITUDE',
      title: 'Items Missing Altitude Level',
      description: `${itemsMissingAltitude.length} item(s) do not have an altitude level assigned.`,
      currentState: `${itemsMissingAltitude.length} of ${items.length} items missing altitude`,
      affectedItems: itemsMissingAltitude,
      remediationAction: 'Assign an altitude level (task through outcome) to each item.',
      remediationRoute: '/prioritization/leveling',
    });
  }
  
  if (candidateTypes.size > 1) {
    const typeList = Array.from(candidateTypes)
      .map(t => `${t} (${typeCounts[t]})`)
      .join(', ');
    
    blockers.push({
      code: 'MIXED_CANDIDATE_TYPES',
      title: 'Mixed Candidate Types',
      description: 'Items in this pool have different candidate types. You cannot prioritize across types without normalization.',
      currentState: `Pool contains: ${typeList}`,
      affectedItems: items
        .filter(i => i.itemCategory && i.itemCategory !== dominantType)
        .map(i => i.id),
      remediationAction: 'Either normalize all items to the same type, or filter the pool to include only one type.',
      remediationRoute: '/prioritization/shape/queue',
    });
  }
  
  if (altitudes.size > 1) {
    const altitudeList = Array.from(altitudes)
      .map(a => `Level ${a} (${altitudeCounts[String(a)]})`)
      .join(', ');
    
    blockers.push({
      code: 'MIXED_ALTITUDES',
      title: 'Mixed Altitude Levels',
      description: 'Items in this pool are at different altitude levels. You cannot prioritize across altitudes without normalization.',
      currentState: `Pool contains: ${altitudeList}`,
      affectedItems: items
        .filter(i => i.altitudeLevel !== undefined && i.altitudeLevel !== dominantAltitude)
        .map(i => i.id),
      remediationAction: 'Either normalize all items to the same altitude, or filter the pool to include only one altitude level.',
      remediationRoute: '/prioritization/leveling',
    });
  }
  
  return {
    isHomogeneous,
    isTypeHomogeneous,
    isAltitudeHomogeneous,
    candidateTypes,
    altitudes,
    itemsMissingType,
    itemsMissingAltitude,
    dominantType,
    dominantAltitude,
    blockers,
    stats: {
      totalItems: items.length,
      typeCounts,
      altitudeCounts,
    },
  };
}

/**
 * Find the most common value in a counts record
 */
function findDominant(counts: Record<string, number>): string | undefined {
  let dominant: string | undefined;
  let maxCount = 0;
  
  for (const [key, count] of Object.entries(counts)) {
    if (count > maxCount) {
      maxCount = count;
      dominant = key;
    }
  }
  
  return dominant;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get a human-readable summary of pool homogeneity status
 */
export function getHomogeneitySummary(result: PoolHomogeneityResult): string {
  if (result.isHomogeneous) {
    const type = result.dominantType || 'untyped';
    const altitude = result.dominantAltitude !== undefined ? `Level ${result.dominantAltitude}` : 'unleveled';
    return `Homogeneous pool: ${result.stats.totalItems} ${type} items at ${altitude}`;
  }
  
  const issues: string[] = [];
  
  if (!result.isTypeHomogeneous) {
    issues.push(`${result.candidateTypes.size} different types`);
  }
  
  if (!result.isAltitudeHomogeneous) {
    issues.push(`${result.altitudes.size} different altitudes`);
  }
  
  if (result.itemsMissingType.length > 0) {
    issues.push(`${result.itemsMissingType.length} missing type`);
  }
  
  if (result.itemsMissingAltitude.length > 0) {
    issues.push(`${result.itemsMissingAltitude.length} missing altitude`);
  }
  
  return `Non-homogeneous pool: ${issues.join(', ')}`;
}

/**
 * Check if a single item would maintain homogeneity if added to a pool
 */
export function wouldMaintainHomogeneity(
  existingResult: PoolHomogeneityResult,
  newItem: Candidate
): { wouldMaintain: boolean; reason?: string } {
  // If pool is already non-homogeneous, adding any item doesn't make it worse
  if (!existingResult.isHomogeneous) {
    return { wouldMaintain: false, reason: 'Pool is already non-homogeneous' };
  }
  
  // Empty pool - any item is fine
  if (existingResult.stats.totalItems === 0) {
    return { wouldMaintain: true };
  }
  
  // Check type
  if (existingResult.dominantType && newItem.itemCategory !== existingResult.dominantType) {
    return {
      wouldMaintain: false,
      reason: `Item type "${newItem.itemCategory}" differs from pool type "${existingResult.dominantType}"`,
    };
  }
  
  // Check altitude
  if (existingResult.dominantAltitude !== undefined && 
      newItem.altitudeLevel !== existingResult.dominantAltitude) {
    return {
      wouldMaintain: false,
      reason: `Item altitude ${newItem.altitudeLevel} differs from pool altitude ${existingResult.dominantAltitude}`,
    };
  }
  
  return { wouldMaintain: true };
}

/**
 * Get items that don't match the pool's dominant type/altitude
 */
export function getNonMatchingItems(
  items: Candidate[],
  result: PoolHomogeneityResult
): Candidate[] {
  return items.filter(item => {
    if (result.dominantType && item.itemCategory !== result.dominantType) {
      return true;
    }
    if (result.dominantAltitude !== undefined && item.altitudeLevel !== result.dominantAltitude) {
      return true;
    }
    return false;
  });
}

/**
 * Suggest actions to make a pool homogeneous
 */
export function getSuggestions(result: PoolHomogeneityResult): string[] {
  const suggestions: string[] = [];
  
  if (result.itemsMissingType.length > 0) {
    suggestions.push(`Assign candidate type to ${result.itemsMissingType.length} item(s)`);
  }
  
  if (result.itemsMissingAltitude.length > 0) {
    suggestions.push(`Assign altitude level to ${result.itemsMissingAltitude.length} item(s)`);
  }
  
  if (result.candidateTypes.size > 1 && result.dominantType) {
    const nonDominantCount = result.stats.totalItems - (result.stats.typeCounts[result.dominantType] || 0);
    suggestions.push(
      `Filter to only "${result.dominantType}" items (remove ${nonDominantCount} items)`,
      `Or normalize all items to a single type`
    );
  }
  
  if (result.altitudes.size > 1 && result.dominantAltitude !== undefined) {
    const nonDominantCount = result.stats.totalItems - (result.stats.altitudeCounts[String(result.dominantAltitude)] || 0);
    suggestions.push(
      `Filter to only Level ${result.dominantAltitude} items (remove ${nonDominantCount} items)`,
      `Or level all items to a single altitude`
    );
  }
  
  return suggestions;
}
